import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  getKpiDataByArea,
  getKpiLinksByArea,
  type KpiAreaApiType,
} from "../../API/KPI/AreaKpi";
import type { AreaKpiSummary } from "../../Types/KPI/IAreaKpiSummary";
import type { KeyResult, KpiLink, KpiProgressNote } from "../../Types/KPI/IAreaKpi";
import styles from "./DiagnosticLinkedKpis.module.css";
import drawerItemStyles from "./SectionsDrawerItem.module.css";
import Tooltip from "../Utils/Tooltip/Tooltip";
import { useClientKpiAccess } from "../../Hooks/useClientKpiAccess";

type Props = {
  areaId: string;
  areaType: KpiAreaApiType;
  /** Increment from parent when KPI summary modal closes to refetch drawer data. */
  refreshToken?: number;
  /** Optional area-load KPI payload (from GetQuestionnaireByAreaId) to avoid extra KPI API calls. */
  prefetchedSummary?: AreaKpiSummary;
  hasKpiEdit?: boolean;
  onOpenKpiSummary?: (keyResultId?: string) => void;
  /**
   * Improve plan side pane: skip drawer-style collapsible header and show the list expanded
   * (parent supplies its own pane chrome).
   */
  embedInPlanPane?: boolean;
};

function sortNotesDesc(notes: KpiProgressNote[] | undefined): KpiProgressNote[] {
  if (!notes?.length) return [];
  return [...notes].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });
}

function latestNote(link: KpiLink | undefined): KpiProgressNote | undefined {
  const sorted = sortNotesDesc(link?.kpiProgressNotes);
  return sorted[0];
}

function statusPillClass(label: string | undefined): string {
  const l = (label ?? "").toLowerCase();
  if (l.includes("exceeded")) return styles.statusExceeded;
  if (l.includes("met") && !l.includes("not")) return styles.statusMet;
  if (l.includes("working")) return styles.statusWorking;
  if (l.includes("will not") || l.includes("not met")) return styles.statusRisk;
  return styles.statusNeutral;
}

function formatResultDate(value: string | undefined): string {
  if (!value) return "—";
  try {
    const d = value.includes("T") ? parseISO(value) : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return format(d, "dd MMM yyyy");
  } catch {
    return "—";
  }
}

const DiagnosticLinkedKpis: React.FC<Props> = ({
  areaId,
  areaType,
  refreshToken = 0,
  prefetchedSummary,
  hasKpiEdit = false,
  onOpenKpiSummary,
  embedInPlanPane = false,
}) => {
  const { canAccessClientKpi, kpiDisabledReason } = useClientKpiAccess();
  const [links, setLinks] = useState<KpiLink[]>([]);
  const [keyResults, setKeyResults] = useState<KeyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  /** Whole "Linked KPIs" drawer block — collapsed by default in diagnostics drawer; open when embedded in a plan pane. */
  const [kpiSectionOpen, setKpiSectionOpen] = useState(embedInPlanPane);

  const load = useCallback(async () => {
    if (!areaId || !canAccessClientKpi) {
      setLoading(false);
      setLinks([]);
      setKeyResults([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (prefetchedSummary) {
        setLinks(prefetchedSummary.links ?? []);
        setKeyResults((prefetchedSummary.keyResults ?? []).filter((k) => !k.archived && !k.deleted));
        return;
      }
      const [linkList, results] = await Promise.all([
        getKpiLinksByArea(areaType, areaId),
        getKpiDataByArea(areaType, areaId),
      ]);
      setLinks(linkList);
      setKeyResults(results.filter((k) => !k.archived && !k.deleted));
    } catch {
      setError("KPI data could not be loaded.");
      setLinks([]);
      setKeyResults([]);
    } finally {
      setLoading(false);
    }
  }, [areaId, areaType, prefetchedSummary, refreshToken, canAccessClientKpi]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canAccessClientKpi) {
      setKpiSectionOpen(false);
    }
  }, [canAccessClientKpi]);

  const linkByKeyResultId = useMemo(() => {
    const m = new Map<string, KpiLink>();
    for (const link of links) {
      if (link.keyResultId) m.set(link.keyResultId, link);
    }
    return m;
  }, [links]);

  const rows = useMemo(() => {
    return keyResults.map((kr) => {
      const link = linkByKeyResultId.get(kr.id);
      const note = latestNote(link);
      const statusLabel = note?.status?.statusLabel?.trim() || "Not Set";
      const progress = note?.status?.progressValue;
      return {
        id: kr.id,
        title: kr.title?.trim() || "No title",
        target: kr.target?.trim() || "—",
        date: formatResultDate(kr.date),
        objectives: kr.parentObjectiveTitles?.filter(Boolean) ?? [],
        statusLabel,
        progress: progress != null && !Number.isNaN(progress) ? progress : null,
      };
    });
  }, [keyResults, linkByKeyResultId]);

  const allCardsExpanded = useMemo(
    () => rows.length > 0 && rows.every((r) => expandedIds.has(r.id)),
    [rows, expandedIds],
  );

  const rowIdsKey = useMemo(
    () => [...rows].map((r) => r.id).sort().join("|"),
    [rows],
  );

  useEffect(() => {
    setExpandedIds(new Set());
    setKpiSectionOpen(embedInPlanPane);
  }, [areaId, areaType, rowIdsKey, embedInPlanPane]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleExpandAllCards = useCallback(() => {
    if (rows.length === 0) return;
    setExpandedIds((prev) => {
      const allOpen = rows.every((r) => prev.has(r.id));
      if (allOpen) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }, [rows]);

  const kpiSectionPanelId = "linked-kpis-drawer-panel";
  const sectionExpanded =
    canAccessClientKpi && (embedInPlanPane || kpiSectionOpen);

  const showKpiToolbar =
    sectionExpanded && !loading && !error && rows.length > 0;

  const emptyMessage = embedInPlanPane
    ? "No key results are linked to this track area."
    : "No key results are linked to this diagnostic.";

  const outerWrapperClass = embedInPlanPane ? styles.embedOuter : drawerItemStyles.sectionOuter;

  const innerShellClass = embedInPlanPane
    ? styles.embedShell
    : [
        drawerItemStyles.container,
        drawerItemStyles.actionItem,
        styles.outerShell,
        !canAccessClientKpi ? styles.clientDisabled : "",
      ]
        .filter(Boolean)
        .join(" ");

  const headerRow = !embedInPlanPane && (
        <div className={styles.outerHeaderRow}>
          <button
            type="button"
            id="linked-kpis-drawer-trigger"
            className={styles.outerToggleTitle}
            aria-expanded={sectionExpanded}
            aria-controls={kpiSectionPanelId}
            aria-disabled={!canAccessClientKpi}
            aria-label={
              !canAccessClientKpi
                ? "Linked KPIs unavailable"
                : kpiSectionOpen
                  ? "Collapse Linked KPIs section"
                  : "Expand Linked KPIs section"
            }
            onClick={() => {
              if (canAccessClientKpi) {
                setKpiSectionOpen((open) => !open);
              }
            }}
          >
            <div
              className={[drawerItemStyles.titleContainer, styles.outerTitleContainer].join(
                " ",
              )}
            >
              <span className={styles.panelTitle}>Linked KPIs</span>
            </div>
          </button>
          <button
            type="button"
            className={styles.outerChevronBtn}
            tabIndex={-1}
            aria-disabled={!canAccessClientKpi}
            aria-label={
              !canAccessClientKpi
                ? "Linked KPIs unavailable"
                : kpiSectionOpen
                  ? "Collapse Linked KPIs section"
                  : "Expand Linked KPIs section"
            }
            onClick={() => {
              if (canAccessClientKpi) {
                setKpiSectionOpen((open) => !open);
              }
            }}
          >
            <span
              className={[styles.outerChevron, sectionExpanded ? styles.chevronOpen : ""]
                .filter(Boolean)
                .join(" ")}
              aria-hidden
            />
          </button>
        </div>
  );

  return (
    <div className={outerWrapperClass}>
      <div className={innerShellClass}>
        {!embedInPlanPane &&
          (canAccessClientKpi ? (
            headerRow
          ) : (
            <Tooltip text={kpiDisabledReason} align="top" disabled={false}>
              {headerRow}
            </Tooltip>
          ))}
        {sectionExpanded && (
          <div
            id={kpiSectionPanelId}
            role="region"
            aria-label="Linked KPIs"
            className={[drawerItemStyles.criteriaItems, styles.innerPanel, embedInPlanPane ? styles.innerPanelEmbed : ""]
              .filter(Boolean)
              .join(" ")}
          >
          {loading && <p className={styles.loading}>Loading…</p>}
          {!loading && error && <p className={styles.error}>{error}</p>}
          {showKpiToolbar && (
            <div className={styles.kpiToolbar}>
              <button
                type="button"
                className={styles.toolbarLink}
                aria-label={
                  allCardsExpanded
                    ? "Collapse all linked KPI cards"
                    : "Expand all linked KPI cards"
                }
                onClick={toggleExpandAllCards}
              >
                {allCardsExpanded ? "Collapse all" : "Expand all"}
              </button>
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <p className={styles.empty}>{emptyMessage}</p>
          )}
          {!loading && !error && rows.length > 0 && (
            <div className={styles.list}>
              {rows.map((row) => {
                const expanded = expandedIds.has(row.id);
                const panelId = `kpi-panel-${row.id}`;
                const triggerId = `kpi-trigger-${row.id}`;
                return (
                  <article
                    key={row.id}
                    className={[styles.card, expanded ? styles.cardExpanded : ""]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <button
                      type="button"
                      id={triggerId}
                      className={[
                        styles.cardToggle,
                        expanded ? styles.cardToggleExpanded : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-expanded={expanded}
                      aria-controls={panelId}
                      aria-label={
                        expanded
                          ? `Collapse details for ${row.title}`
                          : `Expand details for ${row.title}`
                      }
                      onClick={() => toggleExpanded(row.id)}
                    >
                      <span className={styles.cardTitle}>{row.title}</span>
                      <span className={styles.toggleEnd}>
                        <span
                          className={[styles.statusPill, statusPillClass(row.statusLabel)].join(
                            " ",
                          )}
                        >
                          {row.statusLabel}
                        </span>
                        <span
                          className={[styles.chevron, expanded ? styles.chevronOpen : ""]
                            .filter(Boolean)
                            .join(" ")}
                          aria-hidden
                        />
                      </span>
                    </button>
                    {expanded && (
                      <div
                        id={panelId}
                        role="region"
                        aria-labelledby={triggerId}
                        className={styles.cardBody}
                      >
                        <div className={styles.cardGrid}>
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>Target</span>
                            <p className={styles.fieldValue}>{row.target}</p>
                          </div>
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>Target date</span>
                            <p className={styles.fieldValue}>{row.date}</p>
                          </div>
                          {row.objectives.length > 0 && (
                            <div className={styles.fieldSpan}>
                              <span className={styles.fieldLabel}>Linked objectives</span>
                              <p className={styles.fieldValueMuted}>
                                {row.objectives.join(", ")}
                              </p>
                            </div>
                          )}
                          {row.progress != null && (
                            <div className={styles.fieldSpanInline}>
                              <span className={styles.fieldLabel}>Progress</span>
                              <span className={styles.progressValue}>{row.progress}%</span>
                            </div>
                          )}
                          {onOpenKpiSummary && canAccessClientKpi && (
                            <div className={styles.cardActionRow}>
                              <button
                                type="button"
                                className={styles.updateKeyResultLink}
                                onClick={() => onOpenKpiSummary(row.id)}
                              >
                                {hasKpiEdit ? "Edit Key Result" : "View Key Result"}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticLinkedKpis;
